/*
| The topbar search box.
|
| The index is built in the browser from the cached catalog (search-index.js),
| so typing costs no request. Hex strings are matched by shape and open on the
| Sei explorer.
*/

import { esc, short } from './format.js';
import { allHits, createIndexLoader, literalHits } from './search-index.js';

const TYPE_LABEL = { market: 'Market', builder: 'Builder', token: 'Token', address: 'Wallet', transaction: 'Tx' };

function rowHtml(hit, index, isSelected) {
  const label = hit.type === 'address' || hit.type === 'transaction' ? short(hit.label, 10, 8) : hit.label;
  const target = hit.external ? ' target="_blank" rel="noopener noreferrer"' : '';
  return `<a href="${esc(hit.href)}"${target} data-i="${index}"${isSelected ? ' class="sel"' : ''}>` +
    `<span class="ty">${esc(TYPE_LABEL[hit.type] ?? hit.type)}</span><span class="t">${esc(label)}</span>` +
    `<span class="s">${esc(hit.subtitle)}</span></a>`;
}

const messageRow = (text) => `<a style="cursor:default"><span class="ty">—</span><span class="t muted">${esc(text)}</span></a>`;

export function mountSearch({ api, links, render }) {
  const input = document.getElementById('search');
  const dropdown = document.getElementById('search-dd');
  if (!input || !dropdown) return;

  let hits = [];
  let selected = 0;
  const loader = createIndexLoader({
    loadPairs: () => api.allTradingPairs(),
    loadBuilders: () => api.builders(),
    loadTokens: async () => (await api.tokenCustody()).tokens,
  });
  let indexFailed = false;
  let queryToken = 0;

  async function loadIndex() {
    try {
      const index = await loader.load();
      indexFailed = false;
      return index;
    } catch {
      indexFailed = true;
      return [];
    }
  }

  const close = () => dropdown.classList.remove('on');

  function show(nextHits, emptyText) {
    hits = nextHits;
    selected = 0;
    dropdown.innerHTML = hits.length
      ? hits.map((hit, i) => rowHtml(hit, i, i === selected)).join('')
      : messageRow(emptyText);
    dropdown.classList.add('on');
  }

  function moveSelection(step) {
    if (!hits.length) return;
    selected = (selected + step + hits.length) % hits.length;
    dropdown.querySelectorAll('a[data-i]').forEach((a) => a.classList.toggle('sel', Number(a.dataset.i) === selected));
  }

  function open(hit) {
    // Any lookup still in flight belongs to the query just answered.
    queryToken += 1;
    input.value = '';
    close();
    input.blur();

    if (hit.external) {
      window.open(hit.href, '_blank', 'noopener,noreferrer');
      return;
    }
    window.history.pushState({}, '', hit.href);
    render();
  }

  async function onQuery(raw) {
    const query = raw.trim();
    const token = ++queryToken;

    if (!query) {
      hits = [];
      close();
      return;
    }

    // The shape-only hits (a wallet, a tx) show at once; index matches, such as
    // a token pasted by its contract, join them when the index is ready.
    const local = literalHits(query, links);
    if (local.length) show(local, 'No matches');

    const loaded = await loadIndex();
    if (token !== queryToken) return;
    show(allHits(loaded, query, links), indexFailed ? 'Search is offline' : 'No matches');
  }

  input.addEventListener('input', (event) => onQuery(event.target.value));
  input.addEventListener('focus', (event) => {
    loadIndex();
    if (event.target.value.trim()) onQuery(event.target.value);
  });

  input.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      close();
      input.blur();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      moveSelection(event.key === 'ArrowDown' ? 1 : -1);
    } else if (event.key === 'Enter' && hits[selected]) {
      event.preventDefault();
      open(hits[selected]);
    }
  });

  dropdown.addEventListener('click', (event) => {
    const row = event.target.closest('a[data-i]');
    if (!row || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    // The router listens on document for a[href^="/"]; it must not navigate twice.
    event.stopPropagation();
    open(hits[Number(row.dataset.i)]);
  });

  document.addEventListener('click', (event) => {
    if (!event.target.closest('.search')) close();
  });

  document.addEventListener('keydown', (event) => {
    const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement?.tagName ?? '')
      || document.activeElement?.isContentEditable;

    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      input.focus();
      input.select();
    } else if (event.key === '/' && !typing) {
      event.preventDefault();
      input.focus();
    }
  });
}
